import { useEffect, useState, useMemo } from "react"
import { groupByRecibo, groupByPeriodoCpf, formatCurrency, isTotalizadorDeRetorno } from "../../../utils/previdenciario-utils"
import { ReciboCard } from "./recibo-card"
import { TotalizadorPanel } from "./totalizador-panel"
import { RubricasTable } from "./rubricas-table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Search, FileText, Calculator, BarChart3, Receipt, Users, ListTree, Loader2 } from "lucide-react"
import type { EsocialData } from "@/types/analise-previdenciario"
import {
  useFetchEsocialRetornosByPrevidenciario,
  useFetchPrevidenciarioDetalhesById,
  type EsocialRetornoLoteResponse,
} from "@/features/products/services"
import { ESocialRetornosPanel } from "./esocial-retornos-panel"

const EMPTY_ESOCIAL_DATA: EsocialData = {
  rubricas: [],
  verbas: [],
  totalizadosEsocial: [],
  esocialPagamentos: [],
}

function normalizeEsocialData(payload: unknown): EsocialData | null {
  if (!payload || typeof payload !== "object") return null

  const asRecord = payload as Record<string, unknown>
  const candidates = [asRecord, (asRecord.data as Record<string, unknown>) || null].filter(Boolean) as Record<
    string,
    unknown
  >[]

  for (const candidate of candidates) {
    const rubricas = Array.isArray(candidate.rubricas) ? candidate.rubricas : null
    const verbas = Array.isArray(candidate.verbas) ? candidate.verbas : null
    const totalizadosEsocial = Array.isArray(candidate.totalizadosEsocial)
      ? candidate.totalizadosEsocial
      : Array.isArray(candidate.totalizados_esocial)
        ? candidate.totalizados_esocial
        : null
    const esocialPagamentos = Array.isArray(candidate.esocialPagamentos)
      ? candidate.esocialPagamentos
      : Array.isArray(candidate.pagamentos)
        ? candidate.pagamentos
      : Array.isArray(candidate.esocial_pagamentos)
        ? candidate.esocial_pagamentos
        : null

    if (rubricas && verbas && totalizadosEsocial && esocialPagamentos) {
      return {
        rubricas: rubricas as EsocialData["rubricas"],
        verbas: verbas as EsocialData["verbas"],
        totalizadosEsocial: totalizadosEsocial as EsocialData["totalizadosEsocial"],
        esocialPagamentos: esocialPagamentos as EsocialData["esocialPagamentos"],
      }
    }
  }

  return null
}

function toNumberString(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toFixed(2)
  }

  if (typeof value === "string") {
    const normalized = value.trim().replace(",", ".")
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00"
  }

  return "0.00"
}

function toInteger(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value)
  }

  if (typeof value === "string") {
    const parsed = Number.parseInt(value.trim(), 10)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function toStringOrNull(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function buildRetornoTotalizados(lotes: EsocialRetornoLoteResponse[] | undefined): EsocialData["totalizadosEsocial"] {
  if (!Array.isArray(lotes) || lotes.length === 0) {
    return []
  }

  const totalizados = new Map<string, EsocialData["totalizadosEsocial"][number]>()

  for (const lote of lotes) {
    for (const evento of lote.eventosDetalhados ?? []) {
      for (const total of evento.totais ?? []) {
        if (!total || typeof total !== "object") continue

        const totalRecord = total as Record<string, unknown>
        if (toStringOrNull(totalRecord.tipo) !== "S5001") continue

        const nrRecibo = toStringOrNull(totalRecord.nrRecArqBase)
        const cpfTrabalhador = toStringOrNull(totalRecord.cpfTrab)
        const perApur = toStringOrNull(totalRecord.perApur)
        const tpcr = toInteger(totalRecord.tpCR)

        if (!nrRecibo || !cpfTrabalhador || !perApur || tpcr == null) continue

        const key = `${nrRecibo}|${cpfTrabalhador}|${perApur}|${tpcr}`
        totalizados.set(key, {
          tpcr,
          vrcpseg: toNumberString(totalRecord.vrCpSeg),
          vrdescseg: toNumberString(totalRecord.vrDescSeg),
          valor_13: toNumberString(totalRecord.valor_13),
          valor: toNumberString(totalRecord.valor),
          nrRecibo,
          cpfTrabalhador,
          perApur,
          dhProcessamento: evento.dhProcessamento ?? lote.dhRecepcao ?? undefined,
          isRetificado: true,
          isRetorno: true,
        })
      }
    }
  }

  return Array.from(totalizados.values())
}

function mergeEsocialData(
  previdenciarioData: EsocialData | null,
  lotes: EsocialRetornoLoteResponse[] | undefined
): EsocialData | null {
  const retornoTotalizados = buildRetornoTotalizados(lotes)

  if (!previdenciarioData && retornoTotalizados.length === 0) {
    return null
  }

  const baseData: EsocialData = previdenciarioData ?? {
    rubricas: [],
    verbas: [],
    totalizadosEsocial: [],
    esocialPagamentos: [],
  }

  const totalizadosMap = new Map<string, EsocialData["totalizadosEsocial"][number]>()

  for (const totalizado of baseData.totalizadosEsocial) {
    const key = `${totalizado.nrRecibo}|${totalizado.cpfTrabalhador}|${totalizado.perApur}|${totalizado.tpcr}|${totalizado.isRetorno === true ? "retorno" : "original"}`
    totalizadosMap.set(key, totalizado)
  }

  for (const totalizado of retornoTotalizados) {
    const key = `${totalizado.nrRecibo}|${totalizado.cpfTrabalhador}|${totalizado.perApur}|${totalizado.tpcr}|${totalizado.isRetorno === true ? "retorno" : "original"}`
    const existing = totalizadosMap.get(key)

    if (!existing) {
      totalizadosMap.set(key, totalizado)
      continue
    }

    totalizadosMap.set(key, {
      ...existing,
      dhProcessamento: existing.dhProcessamento ?? totalizado.dhProcessamento,
      isRetificado: existing.isRetificado || totalizado.isRetificado,
      isRetorno: existing.isRetorno || totalizado.isRetorno,
      vrcpseg: existing.vrcpseg || totalizado.vrcpseg,
      vrdescseg: existing.vrdescseg || totalizado.vrdescseg,
      valor: existing.valor || totalizado.valor,
      valor_13: existing.valor_13 || totalizado.valor_13,
    })
  }

  return {
    ...baseData,
    totalizadosEsocial: Array.from(totalizadosMap.values()),
  }
}

function summarizeRetornos(lotes: EsocialRetornoLoteResponse[] | undefined) {
  const safeLotes = Array.isArray(lotes) ? lotes : []

  return {
    totalLotes: safeLotes.length,
    totalEventos: safeLotes.reduce((sum, lote) => sum + (lote.totalEventos ?? 0), 0),
    totalEventosProcessados: safeLotes.reduce((sum, lote) => sum + (lote.totalEventosProcessados ?? 0), 0),
    totalOcorrencias: safeLotes.reduce((sum, lote) => sum + (lote.totalOcorrencias ?? 0), 0),
    totalErros: safeLotes.reduce((sum, lote) => sum + (lote.totalErros ?? 0), 0),
  }
}

interface EsocialDashboardProps {
  idPrevidenciario?: string
}

export function EsocialDashboard({ idPrevidenciario }: EsocialDashboardProps) {
  const [searchTerm, setSearchTerm] = useState("")
  const [filterRetificado, setFilterRetificado] = useState<string>("all")
  const { data: previdenciarioData, isLoading: isLoadingPrevidenciario } =
    useFetchPrevidenciarioDetalhesById(idPrevidenciario)
  const {
    data: esocialRetornos,
    isLoading: isLoadingRetornos,
    isError: isEsocialRetornosError,
    error: esocialRetornosError,
  } = useFetchEsocialRetornosByPrevidenciario(idPrevidenciario)

  const realData = useMemo(
    () => mergeEsocialData(normalizeEsocialData(previdenciarioData), esocialRetornos),
    [previdenciarioData, esocialRetornos]
  )
  const dashboardData = realData ?? EMPTY_ESOCIAL_DATA
  const isUsingMockData = !realData
  const retornosResumo = useMemo(() => summarizeRetornos(esocialRetornos), [esocialRetornos])
  const isLoading = isLoadingPrevidenciario || isLoadingRetornos

  const reciboGroups = useMemo(() => groupByRecibo(dashboardData), [dashboardData])
  const totalizadorGroups = useMemo(() => groupByPeriodoCpf(dashboardData), [dashboardData])
  const reciboGroupsWithVerba = useMemo(
    () => reciboGroups.filter((g) => g.verbas.length > 0),
    [reciboGroups]
  )

  const stats = useMemo(() => {
    const totalRubricas = dashboardData.rubricas.length
    const totalVerbas = dashboardData.verbas.length
    const totalTotalizadores = dashboardData.totalizadosEsocial.length
    const uniqueCpfs = new Set(
      dashboardData.totalizadosEsocial.map((t) => t.cpfTrabalhador)
    ).size
    const retificados = dashboardData.totalizadosEsocial.filter(
      (t) => isTotalizadorDeRetorno(t)
    ).length
    const valorTotalVerbas = dashboardData.verbas.reduce(
      (sum, v) => sum + parseFloat(v.value),
      0
    )
    return {
      totalRubricas,
      totalVerbas,
      totalTotalizadores,
      uniqueCpfs,
      retificados,
      valorTotalVerbas,
    }
  }, [dashboardData])

  const filteredReciboGroups = useMemo(() => {
    let result = reciboGroupsWithVerba

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(
        (g) =>
          g.nrRecibo.toLowerCase().includes(term) ||
          g.ideDmDev.toLowerCase().includes(term) ||
          g.verbas.some(
            (v) =>
              v.evento.toLowerCase().includes(term) ||
              (v.codRubr ?? "").toLowerCase().includes(term) ||
              v.value.toLowerCase().includes(term)
          ) ||
          g.rubricas.some(
            (r) =>
              r.dscrRubrica.toLowerCase().includes(term) ||
              r.codRubr.toLowerCase().includes(term)
          ) ||
          g.totalizador?.cpfTrabalhador.includes(term)
      )
    }

    if (filterRetificado === "retificado") {
      result = result.filter((g) => g.totalizador && isTotalizadorDeRetorno(g.totalizador))
    } else if (filterRetificado === "original") {
      result = result.filter((g) => !g.totalizador || !isTotalizadorDeRetorno(g.totalizador))
    }

    return result
  }, [reciboGroupsWithVerba, searchTerm, filterRetificado])

  const filteredTotalizadorGroups = useMemo(() => {
    let result = totalizadorGroups

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(
        (g) =>
          g.cpfTrabalhador.includes(term) ||
          g.perApur.includes(term) ||
          g.totalizadores.some((t) => t.nrRecibo.toLowerCase().includes(term))
      )
    }

    if (filterRetificado === "retificado") {
      result = result.filter((g) =>
        g.totalizadores.some((t) => isTotalizadorDeRetorno(t))
      )
    } else if (filterRetificado === "original") {
      result = result.filter((g) =>
        g.totalizadores.every((t) => !isTotalizadorDeRetorno(t))
      )
    }

    return result
  }, [totalizadorGroups, searchTerm, filterRetificado])

  useEffect(() => {
    if (!filteredTotalizadorGroups.length) {
      return
    }

    // const arquivosPorRecibo = buildArquivosRetornoPorRecibo(esocialRetornos)

    console.groupCollapsed(
      `[Periodo+CPF] ${filteredTotalizadorGroups.length} grupo(s) para id_previdenciario=${idPrevidenciario}`
    )

    filteredTotalizadorGroups.forEach((group) => {


      console.log("[Periodo+CPF][grupo]", {
        perApur: group.perApur,
        cpfTrabalhador: group.cpfTrabalhador,
        nrRecibos: group.nrRecibos,
        totalizadores: group.totalizadores.map((totalizador) => ({
          nrRecibo: totalizador.nrRecibo,
          tpcr: totalizador.tpcr,
          valor: totalizador.valor,
          valor_13: totalizador.valor_13,
          vrcpseg: totalizador.vrcpseg,
          vrdescseg: totalizador.vrdescseg,
          isRetificado: totalizador.isRetificado,
          isRetorno: totalizador.isRetorno === true,
        })),
        recibosRelacionados: group.recibos.map((recibo) => ({
          nrRecibo: recibo.nrRecibo,
          ideDmDev: recibo.ideDmDev,
          rubricas: recibo.rubricas.length,
          verbas: recibo.verbas.length,
          pagamentos: recibo.pagamentos.length,
          dhProcessamento: recibo.dhProcessamento,
        })),
        pagamentosRelacionados: group.pagamentos.map((pagamento) => ({
          nrRecibo: pagamento.nrRecibo,
          ideDmDev: pagamento.ideDmDev,
          perApur: pagamento.perApur,
          cpfBeneficiario: pagamento.cpfBeneficiario,
          vrLiq: pagamento.vrLiq,
        })),
      
      })
    })

    console.groupEnd()
  }, [esocialRetornos, filteredTotalizadorGroups, idPrevidenciario])

  if (!idPrevidenciario) {
    return (
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <p className="text-sm text-warning">Selecione uma apuracao para carregar a analise do eSocial.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Analise eSocial</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Relacoes entre recibos, rubricas, verbas, totalizadores e pagamentos
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            <Badge variant={isUsingMockData ? "outline" : "default"}>
              {isUsingMockData ? "Sem dados do previdenciario" : "Dados carregados"}
            </Badge>
            <Badge variant="outline">
              Status de envio: {retornosResumo.totalLotes}
            </Badge>
            {isEsocialRetornosError && (
              <Badge variant="outline" className="border-destructive/40 text-destructive">
                Falha ao carregar status de envio
              </Badge>
            )}
            <Badge variant="outline">ID: {idPrevidenciario}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4 lg:grid-cols-5">
          <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-chart-1" />
              <span className="text-[10px] text-muted-foreground uppercase">Rubricas</span>
            </div>
            <p className="text-lg font-bold">{stats.totalRubricas}</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-chart-2" />
              <span className="text-[10px] text-muted-foreground uppercase">Verbas</span>
            </div>
            <p className="text-lg font-bold">{stats.totalVerbas}</p>
            <p className="text-[10px] text-muted-foreground">{formatCurrency(stats.valorTotalVerbas)}</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <Calculator className="h-4 w-4 text-chart-4" />
              <span className="text-[10px] text-muted-foreground uppercase">Totalizadores</span>
            </div>
            <p className="text-lg font-bold">{stats.totalTotalizadores}</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-chart-5" />
              <span className="text-[10px] text-muted-foreground uppercase">Trabalhadores</span>
            </div>
            <p className="text-lg font-bold">{stats.uniqueCpfs}</p>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-4 w-4 text-destructive" />
              <span className="text-[10px] text-muted-foreground uppercase">Status Envio</span>
            </div>
            <p className="text-lg font-bold">{stats.retificados}</p>
          </div>
        </div>

        {retornosResumo.totalLotes > 0 && (
          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-1">
            <div className="rounded-lg border bg-card p-3">
              <p className="text-[10px] text-muted-foreground uppercase">Lotes eSocial</p>
              <p className="text-lg font-bold">{retornosResumo.totalLotes}</p>
            </div>
          </div>
        )}

        {isEsocialRetornosError && (
          <div className="mb-6 rounded-lg border border-destructive/40 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">Falha ao carregar o status de envio.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {esocialRetornosError instanceof Error
                ? esocialRetornosError.message
                : "A consulta de esocial_retorno_lotes falhou."}
            </p>
          </div>
        )}

        <Tabs defaultValue="verbas" className="space-y-4">
          <div className="space-y-3">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-2 rounded-xl bg-muted/40 p-1.5 md:max-w-3xl md:grid-cols-4">
              <TabsTrigger value="verbas" className="flex min-h-[72px] flex-col items-start justify-center gap-1 rounded-lg px-3 py-3 text-left text-xs sm:text-sm md:min-h-10 md:flex-row md:items-center md:justify-center md:gap-1.5 md:text-center">
                <Receipt className="h-4 w-4 shrink-0" />
                <span>Verbas</span>
                <Badge variant="secondary" className="ml-0 mt-0.5 text-[10px] px-1.5 py-0 md:ml-1 md:mt-0">
                  {filteredReciboGroups.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="rubricas" className="flex min-h-[72px] flex-col items-start justify-center gap-1 rounded-lg px-3 py-3 text-left text-xs sm:text-sm md:min-h-10 md:flex-row md:items-center md:justify-center md:gap-1.5 md:text-center">
                <ListTree className="h-4 w-4 shrink-0" />
                <span>Rubricas</span>
                <Badge variant="secondary" className="ml-0 mt-0.5 text-[10px] px-1.5 py-0 md:ml-1 md:mt-0">
                  {dashboardData.rubricas.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="totalizadores" className="flex min-h-[72px] flex-col items-start justify-center gap-1 rounded-lg px-3 py-3 text-left text-xs sm:text-sm md:min-h-10 md:flex-row md:items-center md:justify-center md:gap-1.5 md:text-center">
                <Calculator className="h-4 w-4 shrink-0" />
                <span>Per. + CPF</span>
                <Badge variant="secondary" className="ml-0 mt-0.5 text-[10px] px-1.5 py-0 md:ml-1 md:mt-0">
                  {filteredTotalizadorGroups.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="retornos" className="flex min-h-[72px] flex-col items-start justify-center gap-1 rounded-lg px-3 py-3 text-left text-xs sm:text-sm md:min-h-10 md:flex-row md:items-center md:justify-center md:gap-1.5 md:text-center">
                <BarChart3 className="h-4 w-4 shrink-0" />
                <span>Envios</span>
                <Badge variant="secondary" className="ml-0 mt-0.5 text-[10px] px-1.5 py-0 md:ml-1 md:mt-0">
                  {retornosResumo.totalLotes}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <div className="flex flex-col gap-3 rounded-xl border bg-card/60 p-3 md:flex-row md:items-center">
              <div className="relative w-full flex-1 md:min-w-[280px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por recibo, CPF, rubrica..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-sm"
                />
              </div>
              <Select value={filterRetificado} onValueChange={setFilterRetificado}>
                <SelectTrigger className="w-full md:w-56">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="original">Somente originais</SelectItem>
                  <SelectItem value="retificado">Somente com retorno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TabsContent value="verbas" className="space-y-3">
            {filteredReciboGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mb-2" />
                <p className="text-sm">Nenhuma verba envolvida encontrada</p>
              </div>
            ) : (
              filteredReciboGroups.map((group) => (
                <ReciboCard key={group.nrRecibo} group={group} />
              ))
            )}
          </TabsContent>

          <TabsContent value="rubricas">
            <RubricasTable rubricas={dashboardData.rubricas} />
          </TabsContent>

          <TabsContent value="totalizadores" className="space-y-3">
            {filteredTotalizadorGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mb-2" />
                <p className="text-sm">Nenhum totalizador encontrado</p>
              </div>
            ) : (
              filteredTotalizadorGroups.map((group) => (
                <TotalizadorPanel
                  key={`${group.perApur}-${group.cpfTrabalhador}`}
                  group={group}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="retornos" className="space-y-3">
            <ESocialRetornosPanel idPrevidenciario={idPrevidenciario} lotes={esocialRetornos ?? []} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
